import {useMutation} from '@apollo/client';
import {message} from 'antd';
import {useTranslation} from 'react-i18next';
import {
    REVIEW_POD,
    ReviewPodResponse,
    DOWNLOAD_POD_REVIEW_TERMINATED,
    DownloadPodReviewTerminatedResponse
} from '../graphql/mutations/podReviewMutations';
import {getUserInfo} from '../../../../utils/auth';
import { showError } from '../../../../utils/exceptionUtils';
import {PodReviewFiltersTypeInput} from '../graphql/queries/getPodReviewOrders';

export const usePodReviewActions = () => {
    const {t, i18n} = useTranslation();
    const [reviewPod, {loading: reviewLoading}] = useMutation<ReviewPodResponse>(REVIEW_POD);
    const [downloadPodReviewTerminated, {loading: downloadLoading}] = useMutation<DownloadPodReviewTerminatedResponse>(DOWNLOAD_POD_REVIEW_TERMINATED);

    const handleReview = async (orderId: string, isApproved: boolean, reasons?: string[], podManualReviewId?: string, reviewNote?: string) => {
        try {
            // 获取当前用户信息
            const userInfo = getUserInfo();
            const reviewerAccountId = userInfo?.account?.id ? parseInt(userInfo.account.id) : null;

            if (!podManualReviewId) {
                message.error(t('podReview.messages.podManualReviewIdRequired'));
                return false;
            }

            const {data} = await reviewPod({
                variables: {
                    orderId,
                    isApproved,
                    reasons,
                    reviewerAccountId,
                    podManualReviewId,
                    reviewNote: reviewNote || null
                }
            });

            if (data?.reviewPod) {
                const actionText = isApproved ? t('podReview.messages.approved') : t('podReview.messages.disapproved');
                message.success(t('podReview.messages.reviewSuccess', {action: actionText}));
                return true;
            } else {
                const actionText = isApproved ? t('podReview.messages.approve') : t('podReview.messages.disapprove');
                message.error(t('podReview.messages.reviewFailed', {action: actionText}));
                return false;
            }
        } catch (error) {
            console.error('POD Review error:', error);
            showError(error);
            return false;
        }
    };

    const handleDownload = async (filters: PodReviewFiltersTypeInput | undefined) => {
        try {
            // 获取当前语言（从i18n获取，处理可能的格式如 'zh-CN'，只取前两位）
            const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
            
            // 获取用户时区
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            
            const {data} = await downloadPodReviewTerminated({
                variables: {
                    filters,
                    language: currentLanguage,
                    timezone: userTimezone
                }
            });

            const result = data?.downloadPodReviewTerminated;
            if (result?.success) {
                message.success(t('podReview.messages.downloadStarted'));
                return true;
            } else {
                // 根据错误码显示对应的多语言消息
                const rawErrorCode = result?.errorCode || 'DOWNLOAD_FAILED';
                const isExceedLimit = rawErrorCode.startsWith('EXCEED_MAX_LIMIT:');
                const errorCode = isExceedLimit ? 'EXCEED_MAX_LIMIT' : rawErrorCode;
                const errorMessage = t(`podReview.errors.${errorCode}`, {
                    maxLimit: isExceedLimit ? Number(rawErrorCode.split(':')[1]).toLocaleString() : undefined,
                    defaultValue: t('podReview.messages.downloadFailed')
                });
                message.error(errorMessage);
                return false;
            }
        } catch (error) {
            console.error('Download POD Review error:', error);
            showError(error);
            return false;
        }
    };

    return {
        handleReview,
        handleDownload,
        loading: reviewLoading || downloadLoading
    };
};
